import React, { useState } from 'react';
import type { DietPlan } from '../../api/dietApi';
import { WeightChart } from './WeightChart';
import type { WeightMeasurement } from '../../api/dietApi';

interface WeightCardProps {
  measurements: WeightMeasurement[];
  nextControlVisitDate: string | null | undefined;
  onAddMeasurement: () => void;
  onBookVisit?: () => void;
}

const fmt = (n: number) => n.toFixed(1).replace('.', ',');

/**
 * WeightCard — об'єднана картка «Twoja waga + Wizyta kontrolna».
 * Точна копія функції weightCard() з прототипу prototyp-panel-pacjenta_8.html.
 *
 * Стан 0 (0 вимірів): іконка + "Twoja waga" + підпис "Zapisz pierwszy pomiar..." + chip + [split Wizyta kontrolna]
 * Стан 1 (1 вимір): іконка + "Twoja waga" + підпис "Pomiar z DD.MM" + inline число + chip + [split]
 * Стан 2+ (2+ виміри): іконка + "Twoja waga" + "Start X,X kg" + число + delta-badge + графік + chip + [split]
 *
 * Wizyta kontrolna (split) показується тільки якщо nextControlVisitDate є (не null/undefined).
 */
export const WeightCard: React.FC<WeightCardProps> = ({
  measurements,
  nextControlVisitDate,
  onAddMeasurement,
  onBookVisit,
}) => {
  const STYLES = `
    /* ── WeightCard ── */
    .wc-card {
      display: block; width: 100%; text-align: left;
      background: #E6D6FA; border-radius: 24px; padding: 16px; margin-bottom: 12px;
    }
    .wc-rowc { display: flex; align-items: center; gap: 8px; width: 100%; }
    .wc-iconc {
      width: 40px; height: 40px; flex: none; border-radius: 32px; background: #fff;
      display: flex; align-items: center; justify-content: center;
    }
    .wc-rowtxt { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 2px; }
    .wc-rowtxt b {
      font-size: 16px; font-weight: 600; line-height: 140%; color: #361777;
      display: flex; align-items: center; gap: 8px; flex-wrap: wrap; white-space: nowrap;
    }
    .wc-rowtxt span { font-size: 12px; font-weight: 500; line-height: 140%; color: #5C4C86; }

    /* Inline-значення ваги (1 вимір або з кількох) */
    .wc-hero {
      display: flex; align-items: baseline; gap: 4px; margin-left: auto; padding-left: 12px;
    }
    .wc-hero b { font-size: 22px; font-weight: 700; line-height: 1; letter-spacing: -.02em; color: #361777; }
    .wc-hero i { font-style: normal; font-size: 13px; font-weight: 600; color: #786798; }

    /* Delta-badge */
    .wc-delta {
      align-self: center; display: inline-flex; align-items: center; gap: 4px;
      padding: 4px 10px; border-radius: 100px; font-size: 12px; font-weight: 600; white-space: nowrap;
    }
    .wc-delta--down { background: #DFF3E8; color: #1B6B45; }
    .wc-delta--up { background: #E6D6FA; color: #786798; } /* нейтральний фіолетовий, НЕ червоний */

    /* Chip "+ Dodaj pomiar" */
    .wc-chiprow { display: flex; justify-content: flex-end; margin-top: 4px; }
    .wc-chip {
      display: inline-flex; align-items: center; gap: 6px; height: 34px; padding: 0 14px;
      border-radius: 100px; border: 1px solid #D6C1F1; background: #fff;
      font-size: 13px; font-weight: 500; color: #361777; cursor: pointer;
      font-family: inherit; transition: background .15s;
    }
    .wc-chip:active { background: #E6D6FA; }

    /* Split — роздільник перед «Wizyta kontrolna» */
    .wc-split { border-top: 1px solid rgba(54,23,119,.14); margin-top: 14px; padding-top: 14px; }
    .wc-split .wc-rowtxt span { font-size: 11.5px; white-space: nowrap; }

    /* Кнопка «Umów» */
    .wc-pill {
      display: inline-flex; align-items: center; justify-content: center;
      height: 38px; padding: 0 16px; border-radius: 100px; border: none;
      background: #361777; color: #fff; font-size: 14px; font-weight: 500;
      cursor: pointer; font-family: inherit; white-space: nowrap; flex: none; transition: opacity .15s;
    }
    .wc-pill:active { opacity: .85; }
  `;

  const hasMultiple = measurements.length >= 2;
  const hasOne = measurements.length === 1;
  const hasNone = measurements.length === 0;

  const startWeight = hasMultiple ? measurements[0].weight : null;
  const lastMeasurement = measurements.length > 0 ? measurements[measurements.length - 1] : null;
  const diff = hasMultiple && startWeight !== null ? lastMeasurement!.weight - startWeight : 0;
  const isDown = diff <= 0;

  // Chip «+ Dodaj pomiar»
  const chip = (
    <div className="wc-chiprow">
      <button className="wc-chip" onClick={onAddMeasurement}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#361777" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 5v14M5 12h14" />
        </svg>
        Dodaj pomiar
      </button>
    </div>
  );

  // Split «Wizyta kontrolna» — тільки якщо дата є
  const controlVisitSplit = nextControlVisitDate ? (
    <div className="wc-split">
      <div className="wc-rowc">
        <div className="wc-iconc">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#57437E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="4" width="18" height="18" rx="2" />
            <path d="M16 2v4M8 2v4M3 10h18" />
          </svg>
        </div>
        <div className="wc-rowtxt">
          <b>Wizyta kontrolna</b>
          <span>Zalecany termin: {nextControlVisitDate}</span>
        </div>
        <button className="wc-pill" onClick={onBookVisit}>Umów</button>
      </div>
    </div>
  ) : null;

  // Іконка ваги (терези/графік)
  const scaleIcon = (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#57437E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 17l5-5 4 3 6-7" />
      <circle cx="8" cy="12" r="1" />
    </svg>
  );

  return (
    <>
      <style>{STYLES}</style>
      <div className="wc-card">

        {/* Стан 0: немає жодного виміру */}
        {hasNone && (
          <>
            <div className="wc-rowc">
              <div className="wc-iconc">{scaleIcon}</div>
              <div className="wc-rowtxt">
                <b>Twoja waga</b>
                <span>Zapisz pierwszy pomiar, żeby widzieć postęp</span>
              </div>
            </div>
            {chip}
          </>
        )}

        {/* Стан 1: один вимір — inline число без графіка */}
        {hasOne && lastMeasurement && (
          <>
            <div className="wc-rowc">
              <div className="wc-iconc">{scaleIcon}</div>
              <div className="wc-rowtxt">
                <b>Twoja waga</b>
                <span>Pomiar z {lastMeasurement.date}</span>
              </div>
              <div className="wc-hero">
                <b>{fmt(lastMeasurement.weight)}</b>
                <i>kg</i>
              </div>
            </div>
            {chip}
          </>
        )}

        {/* Стан 2+: кілька вимірів — з графіком і delta */}
        {hasMultiple && lastMeasurement && startWeight !== null && (
          <>
            <div className="wc-rowc">
              <div className="wc-iconc">{scaleIcon}</div>
              <div className="wc-rowtxt">
                <b>Twoja waga</b>
                <span>Start {fmt(startWeight)} kg</span>
              </div>
              <div className="wc-hero">
                <b>{fmt(lastMeasurement.weight)}</b>
                <i>kg</i>
              </div>
              <span className={`wc-delta ${isDown ? 'wc-delta--down' : 'wc-delta--up'}`}>
                {isDown ? '↓' : '↑'} {fmt(Math.abs(diff))} kg
              </span>
            </div>
            <WeightChart measurements={measurements} />
            {chip}
          </>
        )}

        {/* Wizyta kontrolna (split) */}
        {controlVisitSplit}
      </div>
    </>
  );
};
